import React from 'react';
import { FiArrowRight, FiDollarSign } from 'react-icons/fi';
import { formatMoney } from '../utils/money';
import { scoreBadgeClasses, scoreTone } from '../utils/collectionScore';

const TONE = {
  red: {
    dark: 'border-red-800/60 bg-red-950/30 hover:bg-red-950/50',
    light: 'border-red-200 bg-red-50 hover:bg-red-100/80',
    badgeDark: 'bg-red-900/60 text-red-200',
    badgeLight: 'bg-red-100 text-red-800',
  },
  amber: {
    dark: 'border-amber-800/60 bg-amber-950/30 hover:bg-amber-950/50',
    light: 'border-amber-200 bg-amber-50 hover:bg-amber-100/80',
    badgeDark: 'bg-amber-900/60 text-amber-200',
    badgeLight: 'bg-amber-100 text-amber-800',
  },
  sky: {
    dark: 'border-sky-800/60 bg-sky-950/30 hover:bg-sky-950/50',
    light: 'border-sky-200 bg-sky-50 hover:bg-sky-100/80',
    badgeDark: 'bg-sky-900/60 text-sky-200',
    badgeLight: 'bg-sky-100 text-sky-800',
  },
};

function toneClasses(tone, modoOscuro) {
  const palette = TONE[tone] || TONE.amber;
  return {
    row: modoOscuro ? palette.dark : palette.light,
    badge: modoOscuro ? palette.badgeDark : palette.badgeLight,
  };
}

function CompactBandeja({
  cobrarDia,
  alertItems,
  modoOscuro,
  onOpen,
  hasProfesional,
  box,
  textPri,
  textSec,
  textMuted,
}) {
  const tasks = cobrarDia?.tasks || [];
  const hasCobrar = tasks.length > 0;
  const hasAlerts = alertItems.length > 0;

  if (!hasCobrar && !hasAlerts) return null;

  const topTasks = tasks.slice(0, 2);
  const topAlerts = alertItems.slice(0, hasCobrar ? 1 : 2);

  return (
    <section className={`rounded-xl border ${box}`}>
      <div className="px-3 sm:px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <div className="flex items-start gap-2.5 min-w-0 flex-1">
          <div className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
            modoOscuro ? 'bg-[#1F80FF]/15 text-[#7eb8ff]' : 'bg-[#1F80FF]/10 text-[#004AAD]'
          }`}>
            <FiDollarSign className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <h2 className={`text-sm font-semibold ${textPri}`}>Cobros pendientes</h2>
              {hasCobrar && cobrarDia?.totalPendientes > 0 && (
                <span className={`text-[11px] font-medium ${textMuted}`}>
                  {cobrarDia.totalPendientes} impaga{cobrarDia.totalPendientes === 1 ? '' : 's'}
                  {cobrarDia.totalArs > 0 ? ` · ${formatMoney(cobrarDia.totalArs)}` : ''}
                </span>
              )}
            </div>
            <p className={`text-xs mt-0.5 ${textSec}`}>
              {hasCobrar
                ? 'Prioridad del día. El detalle completo está en Cobrar.'
                : 'Revisá estos puntos del mes.'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => onOpen('/cobranzas')}
          className={`inline-flex shrink-0 items-center justify-center gap-1 rounded-lg border px-3 py-2 text-xs font-semibold min-h-[40px] ${
            modoOscuro
              ? 'border-gray-700 text-gray-200 hover:bg-gray-800'
              : 'border-gray-200 text-gray-700 hover:bg-gray-50'
          }`}
        >
          Ir a Cobrar
          <FiArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>

      {(topTasks.length > 0 || topAlerts.length > 0) && (
        <div className={`border-t px-3 sm:px-4 py-2.5 flex flex-wrap gap-2 ${
          modoOscuro ? 'border-gray-800' : 'border-gray-100'
        }`}>
          {topTasks.map((item) => {
            const { badge } = toneClasses(item.tone, modoOscuro);
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onOpen(item.href)}
                className={`inline-flex max-w-full items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left transition-colors ${
                  modoOscuro
                    ? 'border-gray-700 bg-gray-800/50 hover:bg-gray-800'
                    : 'border-gray-200 bg-gray-50 hover:bg-white'
                }`}
              >
                {item.priorityRank && (
                  <span className={`text-[10px] font-bold tabular-nums ${textMuted}`}>
                    #{item.priorityRank}
                  </span>
                )}
                <span className={`text-xs font-medium truncate max-w-[180px] sm:max-w-[220px] ${textPri}`}>
                  {item.title}
                </span>
                <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase shrink-0 ${badge}`}>
                  {item.priorityLabel || 'Cobrar'}
                </span>
                {hasProfesional && item.clientScore != null && (
                  <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold shrink-0 ${scoreBadgeClasses(scoreTone(item.clientScore), modoOscuro)}`}>
                    {item.clientScore}
                  </span>
                )}
              </button>
            );
          })}
          {topAlerts.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => onOpen(item.href)}
              className={`inline-flex max-w-full items-center rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                modoOscuro
                  ? 'border-gray-700 text-gray-300 hover:bg-gray-800'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {item.title}
            </button>
          ))}
          {hasCobrar && cobrarDia?.hasMore && (
            <span className={`text-[11px] self-center ${textMuted}`}>
              +{Math.max(cobrarDia.totalPendientes - topTasks.length, 0)} más en Cobrar
            </span>
          )}
        </div>
      )}
    </section>
  );
}

export default function BandejaMes({
  cobrarDia,
  alertItems = [],
  modoOscuro,
  onOpen,
  hasProfesional = false,
  variant = 'full',
}) {
  const box = modoOscuro ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white';
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textSec = modoOscuro ? 'text-gray-300' : 'text-gray-600';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const tasks = cobrarDia?.tasks || [];
  const hasCobrar = tasks.length > 0;
  const hasAlerts = alertItems.length > 0;

  if (variant === 'compact') {
    return (
      <CompactBandeja
        cobrarDia={cobrarDia}
        alertItems={alertItems}
        modoOscuro={modoOscuro}
        onOpen={onOpen}
        hasProfesional={hasProfesional}
        box={box}
        textPri={textPri}
        textSec={textSec}
        textMuted={textMuted}
      />
    );
  }

  if (!hasCobrar && !hasAlerts) {
    return null;
  }

  const summaryParts = [];
  if (cobrarDia?.totalPendientes > 0) {
    summaryParts.push(
      `${cobrarDia.totalPendientes} impaga${cobrarDia.totalPendientes === 1 ? '' : 's'}`
    );
  }
  if (cobrarDia?.totalArs > 0) {
    summaryParts.push(`${formatMoney(cobrarDia.totalArs)} pendientes en ARS`);
  }

  return (
    <section className={`rounded-2xl border overflow-hidden ${box}`}>
      <div className={`px-4 py-3 border-b ${modoOscuro ? 'border-gray-800' : 'border-gray-100'}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className={`text-sm font-semibold ${textPri}`}>Qué cobrar hoy</h2>
            <p className={`text-xs mt-0.5 ${textSec}`}>
              {hasCobrar
                ? (cobrarDia?.smartPriority
                  ? 'Orden inteligente: promesas, vencidas, score y monto.'
                  : 'Priorizamos vencidas, promesas y lo que vence pronto.')
                : 'También conviene revisar estos puntos del mes.'}
            </p>
            {summaryParts.length > 0 && (
              <p className={`text-[11px] mt-1 font-medium ${textMuted}`}>
                {summaryParts.join(' · ')}
              </p>
            )}
          </div>
          {hasCobrar && (
            <button
              type="button"
              onClick={() => onOpen('/cobranzas')}
              className={`inline-flex shrink-0 items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${
                modoOscuro
                  ? 'border-gray-700 text-gray-200 hover:bg-gray-800'
                  : 'border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Ir a Cobrar
              <FiArrowRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {hasCobrar && (
        <div className={`divide-y ${modoOscuro ? 'divide-gray-800' : 'divide-gray-100'}`}>
          {tasks.map((item) => {
            const { row, badge } = toneClasses(item.tone, modoOscuro);
            const showScore = hasProfesional && item.clientScore != null;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onOpen(item.href)}
                className={`w-full text-left px-4 py-3 transition-colors border-l-2 border-transparent hover:border-[#1F80FF] ${row}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex items-start gap-2.5">
                    {item.priorityRank && (
                      <span className={`mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
                        modoOscuro ? 'bg-[#1F80FF]/20 text-[#7eb8ff]' : 'bg-[#1F80FF]/10 text-[#004AAD]'
                      }`}>
                        {item.priorityRank}
                      </span>
                    )}
                    <div className="min-w-0">
                      <p className={`text-sm font-semibold truncate ${textPri}`}>{item.title}</p>
                      {item.meta && (
                        <p className={`text-[11px] mt-0.5 ${textMuted}`}>{item.meta}</p>
                      )}
                      <p className={`text-xs mt-1 ${textSec}`}>{item.detail}</p>
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${badge}`}>
                      {item.priorityLabel || 'Cobrar'}
                    </span>
                    {showScore && (
                      <span className={`rounded-lg px-2 py-0.5 text-[10px] font-bold ${scoreBadgeClasses(scoreTone(item.clientScore), modoOscuro)}`}>
                        Score {item.clientScore}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {hasCobrar && cobrarDia?.hasMore && (
        <button
          type="button"
          onClick={() => onOpen('/cobranzas')}
          className={`w-full px-4 py-2.5 text-xs font-semibold border-t ${
            modoOscuro
              ? 'border-gray-800 text-[#7eb8ff] hover:bg-gray-800/60'
              : 'border-gray-100 text-[#1F80FF] hover:bg-gray-50'
          }`}
        >
          Ver más en la bandeja de Cobrar
        </button>
      )}

      {hasAlerts && (
        <div className={hasCobrar ? `border-t ${modoOscuro ? 'border-gray-800' : 'border-gray-100'}` : ''}>
          {hasCobrar && (
            <p className={`px-4 pt-3 text-[11px] font-semibold uppercase tracking-wide ${textMuted}`}>
              También revisar
            </p>
          )}
          <div className={`divide-y ${modoOscuro ? 'divide-gray-800' : 'divide-gray-100'}`}>
            {alertItems.map((item) => {
              const { row } = toneClasses(item.tone, modoOscuro);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onOpen(item.href)}
                  className={`w-full text-left px-4 py-3 transition-colors ${row}`}
                >
                  <p className={`text-sm font-semibold ${textPri}`}>{item.title}</p>
                  {item.detail && (
                    <p className={`text-xs mt-0.5 ${textSec}`}>{item.detail}</p>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
